import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckSquare, Gift, ClipboardCopy, Check } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { SERVICES } from "../data/services";
import { api } from "../lib/api";

const order = ["interior", "exterior", "full"];
const nt = { target: "_blank", rel: "noopener noreferrer" };
const VALUE_BADGE = {
  signature_interior: "$75 of extra value",
  wash_clay_coat: "$100 of extra value",
  signature_full: "$200+ of extra value",
};

export default function ServiceDetail() {
  const { type } = useParams();
  const s = SERVICES[type] || SERVICES.full;
  const addonHref = type === "interior" ? "/add-ons/interior" : type === "exterior" ? "/add-ons/exterior" : "/add-ons/interior?highlight=exterior";
  const [coupon, setCoupon] = useState("");
  const [copied, setCopied] = useState(false);

  const claimCoupon = async () => {
    try {
      const { data } = await api.post("/claim-coupon", { kind: "signature_upgrade" });
      setCoupon(data.code);
      toast.success("Coupon claimed! Use it at checkout for your free gas-cap/exhaust clean.");
    } catch { toast.error("Could not claim coupon — please try again."); }
  };
  const copyCoupon = async () => {
    try { await navigator.clipboard.writeText(coupon); setCopied(true); toast.success("Copied!"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed — select the code and copy manually."); }
  };

  const showChecklist = s.phases && s.phases.length > 0;

  return (
    <Layout>
      <section className="relative overflow-hidden">
        <img src={s.hero} alt={s.name} style={{ objectPosition: s.heroPos || "center" }} className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-[#050505]/50" />
        <div className="relative max-w-4xl mx-auto px-6 py-24 md:py-32">
          <p className="text-xs tracking-[0.3em] uppercase text-pink-500 font-bold mb-4">Our Services</p>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter">{s.name}</h1>
          <p className="text-lg text-zinc-300 mt-5 max-w-2xl leading-relaxed">{s.tagline}</p>
          <p className="mt-4 text-3xl font-extrabold text-pink-500">{s.range}</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 -mt-6 relative z-10">
        <div className="flex flex-wrap gap-3">
          {order.map((k) => (
            <Link key={k} to={`/services/${k}`} className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${type === k ? "bg-pink-600 text-white" : "border border-white/20 text-zinc-300 hover:border-white/40"}`} data-testid={`service-tab-${k}`}>{SERVICES[k].name}</Link>
          ))}
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-6 py-12">
        <p className="text-zinc-300 leading-relaxed text-lg">{s.intro}</p>

        {/* Why Signature wins + our checklist, combined at the top */}
        {(s.valuePitch || showChecklist) && (
          <div className="mt-6 rounded-2xl border border-pink-500/50 bg-gradient-to-br from-[#1a0710] to-[#121212] p-6" data-testid="value-pitch">
            <p className="text-xs uppercase tracking-widest text-pink-500 font-bold mb-2">Why Signature wins</p>
            {s.valuePitch && <p className="text-zinc-100 leading-relaxed font-medium">{s.valuePitch}</p>}
            {showChecklist && (
              <div className="mt-5 space-y-3">
                {s.phases.map((ph, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }} className="rounded-xl border border-white/10 bg-[#0d0d0d] p-4" data-testid={`phase-${i}`}>
                    <h4 className="font-bold text-sm">{ph.name}</h4>
                    <ul className="mt-2 grid gap-2 sm:grid-cols-2">{ph.steps.map((st, j) => (<li key={j} className="text-sm text-zinc-300 flex gap-2 items-start"><CheckSquare size={16} className="text-pink-500 shrink-0 mt-0.5" />{st}</li>))}</ul>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {s.packages.filter(Boolean).map((p) => (
            <div key={p.key} className="relative rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid={`svc-pkg-${p.key}`}>
              {VALUE_BADGE[p.key] && (
                <span className="absolute -top-3 right-4 rounded-full bg-pink-600 text-white text-xs font-bold px-3 py-1 shadow-lg" data-testid={`value-badge-${p.key}`}>{VALUE_BADGE[p.key]}</span>
              )}
              <h3 className="text-xl font-bold">{p.name}</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{p.desc}</p>
              <div className="mt-5 flex gap-8">
                <div><p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Sedan</p><p className="text-2xl font-extrabold">${p.sedan}</p></div>
                <div><p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">SUV / Truck</p><p className="text-2xl font-extrabold text-pink-500">${p.suv}</p></div>
              </div>
              {p.key === "signature_full" && (
                <div className="mt-5 rounded-xl border border-pink-500/40 bg-[#1a0710] p-4">
                  <p className="text-sm font-semibold text-pink-300 flex items-center gap-2"><Gift size={16} /> FREE gas-cap / exhaust-tip cleaning</p>
                  {!coupon ? (
                    <button onClick={claimCoupon} className="mt-3 w-full bg-pink-600 hover:bg-pink-500 rounded-full py-2.5 text-sm font-bold transition-colors active:scale-95" data-testid="claim-coupon-btn">Claim free coupon</button>
                  ) : (
                    <div className="mt-3">
                      <div className="flex items-center gap-2 rounded-lg border border-pink-500/50 bg-[#050505] p-2">
                        <span className="flex-1 text-center font-mono font-extrabold text-pink-500" data-testid="claimed-coupon-code">{coupon}</span>
                        <button onClick={copyCoupon} className="rounded-md bg-white/10 hover:bg-pink-600 px-3 py-1.5 text-xs font-semibold flex items-center gap-1 transition-colors" data-testid="copy-coupon-btn">{copied ? <><Check size={13} /> Copied</> : <><ClipboardCopy size={13} /> Copy</>}</button>
                      </div>
                      <p className="mt-2 text-xs text-zinc-400">Paste this code in the "Coupon / Discount code" box at checkout.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/book" {...nt} className="bg-pink-600 hover:bg-pink-500 text-white font-semibold rounded-full px-8 py-4 transition-colors active:scale-95 flex items-center gap-2" data-testid="svc-book-btn">Book this detail <ArrowRight size={18} /></Link>
          <Link to="/pricing" {...nt} className="border border-white/25 hover:border-white/50 rounded-full px-8 py-4 transition-colors">Full pricing</Link>
          <Link to={addonHref} {...nt} className="border border-white/25 hover:border-white/50 rounded-full px-8 py-4 transition-colors">Browse add-ons</Link>
        </div>
      </article>
    </Layout>
  );
}
